import React, { useState, useEffect } from "react";
import { useUserContext } from "../config/UserContext";
import { useLoadScript } from "@react-google-maps/api";
import AddressInput from "./AddressInput";

const libraries: any = ['places'];

interface AdvancedGrantorModalProps {
  open: boolean;
  type: 'individual' | 'trust' | 'married' | 'company';
  onClose: () => void;
  onSave: (entities: any[]) => void | Promise<void>;
  existingEntities?: any[];
  config?: { showMaritalStatus?: boolean; [key: string]: any };
}



const maritalOptions = ['Single', 'Married', 'Unknown'] as const;
const memberRoleOptions = ['Managing Member', 'Member', 'Sole Member'] as const;
const SUFFIXES = new Set(['JR', 'JR.', 'SR', 'SR.', 'II', 'III', 'IV', 'V']);
const SURNAME_PREFIXES = new Set(['VAN', 'VON', 'DE', 'DEL', 'DELLA', 'LA', 'LE', 'ST', 'ST.', 'SAN', 'SANTA', 'MC', 'MAC', 'EL', 'AL']);

const smartSplitName = (fullName: string): { firstName: string; middleName: string; lastName: string } => {
  const raw = (fullName || '').trim();
  if (!raw) return { firstName: '', middleName: '', lastName: '' };
  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 1) return { firstName: tokens[0], middleName: '', lastName: '' };
  if (tokens.length === 2) return { firstName: tokens[0], middleName: '', lastName: tokens[1] };

  let suffix = '';
  if (tokens.length >= 3 && SUFFIXES.has(tokens[tokens.length - 1].toUpperCase())) {
    suffix = tokens.pop()!;
  }
  if (tokens.length === 2) {
    return {
      firstName: tokens[0],
      middleName: '',
      lastName: suffix ? `${tokens[1]} ${suffix}` : tokens[1],
    };
  }
  const firstName = tokens[0];
  let lastStart = tokens.length - 1;
  while (lastStart > 1 && SURNAME_PREFIXES.has(tokens[lastStart - 1].toUpperCase())) {
    lastStart--;
  }
  const middleName = tokens.slice(1, lastStart).join(' ');
  const lastTokens = tokens.slice(lastStart);
  if (suffix) lastTokens.push(suffix);
  return {
    firstName,
    middleName,
    lastName: lastTokens.join(' '),
  };
};

const resolveNamePayload = (personForm: any, isSplitMode: boolean) => {
  if (isSplitMode) {
    const firstName = (personForm?.firstName || '').trim();
    const middleName = (personForm?.middleName || '').trim();
    const lastName = (personForm?.lastName || '').trim();
    const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ').trim() || (personForm?.fullName || '').trim();
    return { fullName, firstName, middleName, lastName };
  }
  const fullName = (personForm?.fullName || [personForm?.firstName, personForm?.middleName, personForm?.lastName].filter(Boolean).join(' ')).trim();
  const split = smartSplitName(fullName);
  return {
    fullName,
    firstName: split.firstName,
    middleName: split.middleName,
    lastName: split.lastName,
  };
};

const AdvancedGrantorModal: React.FC<AdvancedGrantorModalProps> = ({ open, type, onClose, onSave, existingEntities, config }) => {
  const { document } = useUserContext();
  const { isLoaded } = useLoadScript({ googleMapsApiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '', libraries, version: "weekly" });
  const generateId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(7));
  const [groupId] = useState(existingEntities?.[0]?.groupId || generateId());
  
  // State for forms
  const [form1, setForm1] = useState<any>({});
  const [form2, setForm2] = useState<any>({}); // For married couple spouse
  const [sharedContact, setSharedContact] = useState<any>({});
  const [trustees, setTrustees] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [maritalStatus, setMaritalStatus] = useState<string>('Unknown');
  const showMaritalStatus = config?.showMaritalStatus ?? false;
  const allowNonParticipating = Boolean(config?.allowNonParticipating || config?.allowNonParticipants);
  const isSplitNameMode = config?.nameFormat === 'split' || (Array.isArray(config?.fields) && config.fields.includes('firstName'));

  useEffect(() => {
    if (open) {
      if (existingEntities && existingEntities.length > 0) {
        if (type === 'trust') {
          // Trust container has the trust name
          const trustContainer = existingEntities.find((e: any) => e.type === 'trust');
          setForm1({ name: trustContainer?.metadata?.name || '' });
          // Trustees are separate grantor rows when loaded from server
          const trusteeRows = existingEntities.filter((e: any) => e.type === 'trustee');
          if (trusteeRows.length > 0) {
            // Loaded from server — trustees are separate rows
            setTrustees(trusteeRows.map((t: any) => {
              const fn = t.metadata?.fullName || [t.metadata?.firstName, t.metadata?.middleName, t.metadata?.lastName].filter(Boolean).join(' ').trim();
              const sp = smartSplitName(fn);
              return {
                id: t.metadata?.trusteeId || t.lookup,
                fullName: fn,
                firstName: t.metadata?.firstName || sp.firstName,
                middleName: t.metadata?.middleName || sp.middleName,
                lastName: t.metadata?.lastName || sp.lastName,
                phone: t.metadata?.phone || '',
                email: t.metadata?.email || '',
                address: t.metadata?.address || '',
              };
            }));
          } else {
            // Might be from initial local creation (metadata.trustees array)
            setTrustees(trustContainer?.metadata?.trustees || []);
          }
        } else if (type === 'company') {
          const companyContainer = existingEntities.find((e: any) => e.type === 'company');
          setForm1({ name: companyContainer?.metadata?.name || '' });
          const memberRows = existingEntities.filter((e: any) => e.type === 'member');
          if (memberRows.length > 0) {
            setMembers(memberRows.map((m: any) => {
              const fn = m.metadata?.fullName || [m.metadata?.firstName, m.metadata?.middleName, m.metadata?.lastName].filter(Boolean).join(' ').trim();
              const sp = smartSplitName(fn);
              return {
                id: m.metadata?.memberId || m.lookup,
                fullName: fn,
                firstName: m.metadata?.firstName || sp.firstName,
                middleName: m.metadata?.middleName || sp.middleName,
                lastName: m.metadata?.lastName || sp.lastName,
                role: m.metadata?.role || 'Member',
                phone: m.metadata?.phone || '',
                email: m.metadata?.email || '',
                address: m.metadata?.address || '',
              };
            }));
          } else {
            setMembers(companyContainer?.metadata?.members || []);
          }
        } else {
          const individuals = existingEntities.filter((e: any) => e.type !== 'couple');
          const m1 = individuals[0]?.metadata || {};
          const sp1 = smartSplitName(m1.fullName || [m1.firstName, m1.middleName, m1.lastName].filter(Boolean).join(' '));
          setForm1({
            ...m1,
            fullName: m1.fullName || [m1.firstName, m1.middleName, m1.lastName].filter(Boolean).join(' ').trim(),
            firstName: m1.firstName || sp1.firstName,
            middleName: m1.middleName || sp1.middleName,
            lastName: m1.lastName || sp1.lastName,
            isNonParticipating: m1.claimantType === 'Non-Participant' || Boolean(m1.isNonParticipating),
          });
          if (individuals.length > 1) {
            const m2 = individuals[1]?.metadata || {};
            const sp2 = smartSplitName(m2.fullName || [m2.firstName, m2.middleName, m2.lastName].filter(Boolean).join(' '));
            setForm2({
              ...m2,
              fullName: m2.fullName || [m2.firstName, m2.middleName, m2.lastName].filter(Boolean).join(' ').trim(),
              firstName: m2.firstName || sp2.firstName,
              middleName: m2.middleName || sp2.middleName,
              lastName: m2.lastName || sp2.lastName,
              isNonParticipating: m2.claimantType === 'Non-Participant' || Boolean(m2.isNonParticipating),
            });
          }
          setSharedContact({
            address: individuals[0]?.metadata?.address || existingEntities[0]?.metadata?.address || '',
          });
          // Restore marital status from existing entity if editing
          if (type === 'individual' && individuals[0]?.metadata?.maritalStatus) {
            setMaritalStatus(individuals[0].metadata.maritalStatus);
          }
        }
      } else {
        setForm1({});
        setForm2({});
        setSharedContact({});
        setTrustees([]);
        setMembers([]);
        setMaritalStatus('Unknown');
      }
    }
  }, [open, existingEntities, type]);

  if (!open) return null;

  const handleSave = async () => {
    const isPhoneValid = (phone: string) => phone && phone.length === 14;

    if (type === 'trust') {
        if (trustees.some(t => !isPhoneValid(t.phone))) {
           alert('All phone numbers must be provided and fully formatted (14 characters).');
           return;
        }
    } else if (type === 'company') {
        if (members.some(m => !isPhoneValid(m.phone))) {
           alert('All phone numbers must be provided and fully formatted (14 characters).');
           return;
        }
    } else if (type === 'individual') {
        if (!isPhoneValid(form1.phone)) {
           alert('Phone number must be provided and fully formatted (14 characters).');
           return;
        }
    } else if (type === 'married') {
        if (sharedContact) {
           if (!isPhoneValid(form1.phone)) {
               alert('Phone number must be provided and fully formatted (14 characters).');
               return;
           }
        } else {
           if (!isPhoneValid(form1.phone) || !isPhoneValid(form2.phone)) {
               alert('Both phone numbers must be provided and fully formatted (14 characters).');
               return;
           }
        }
    }

    setIsSaving(true);
    try {
    const entities = [];
    
    if (type === 'trust') {
      entities.push({
        lookup: existingEntities?.[0]?.lookup || generateId(),
        type: 'trust',
        groupId,
        status: 'active',
        active: true,
        documentLookup: document?.lookup,
        metadata: {
          name: form1.name,
          claimantType: 'Participant',
          trustees: trustees.map(t => ({
            ...t,
            claimantType: 'Participant',
            ...resolveNamePayload(t, isSplitNameMode),
          }))
        }
      });
    } else if (type === 'company') {
      entities.push({
        lookup: existingEntities?.find((e: any) => e.type === 'company')?.lookup || existingEntities?.[0]?.lookup || generateId(),
        type: 'company',
        groupId,
        status: 'active',
        active: true,
        documentLookup: document?.lookup,
        metadata: {
          name: form1.name,
          claimantType: 'Participant',
          members: members.map(m => ({
            ...m,
            role: m.role || 'Member',
            claimantType: 'Participant',
            ...resolveNamePayload(m, isSplitNameMode),
          }))
        }
      });
    } else if (type === 'individual') {
      const nameFields1 = resolveNamePayload(form1, isSplitNameMode);
      entities.push({
        lookup: existingEntities?.[0]?.lookup || generateId(),
        type: 'individual',
        groupId,
        status: 'active',
        active: true,
        documentLookup: document?.lookup,
        metadata: {
          ...nameFields1,
          email: form1.email,
          phone: form1.phone,
          address: form1.address,
          maritalStatus: showMaritalStatus ? maritalStatus : 'Unknown',
          claimantType: allowNonParticipating && form1.isNonParticipating ? 'Non-Participant' : 'Participant',
        }
      });
    } else if (type === 'married') {
      // Couple container row (non-photographable)
      entities.push({
        lookup: existingEntities?.find((e: any) => e.type === 'couple')?.lookup || generateId(),
        type: 'couple',
        groupId,
        status: 'active',
        active: true,
        biometricType: 'enroll',
        documentLookup: document?.lookup,
        metadata: null,
      });
      // Spouse 1
      const existingIndividuals = existingEntities?.filter((e: any) => e.type === 'individual' || e.type !== 'couple') || [];
      const nameFields1 = resolveNamePayload(form1, isSplitNameMode);
      const nameFields2 = resolveNamePayload(form2, isSplitNameMode);
      entities.push({
        lookup: existingIndividuals[0]?.lookup || generateId(),
        type: 'individual',
        groupId,
        status: 'active',
        active: true,
        biometricType: 'enroll',
        documentLookup: document?.lookup,
        metadata: {
          ...nameFields1,
          email: form1.email,
          phone: form1.phone,
          address: sharedContact.address,
          maritalStatus: 'Married',
          claimantType: allowNonParticipating && form1.isNonParticipating ? 'Non-Participant' : 'Participant',
        }
      });
      // Spouse 2
      entities.push({
        lookup: existingIndividuals[1]?.lookup || generateId(),
        type: 'individual',
        groupId,
        status: 'active',
        active: true,
        biometricType: 'enroll',
        documentLookup: document?.lookup,
        metadata: {
          ...nameFields2,
          email: form2.email,
          phone: form2.phone,
          address: sharedContact.address,
          maritalStatus: 'Married',
          claimantType: allowNonParticipating && form2.isNonParticipating ? 'Non-Participant' : 'Participant',
        }
      });
    }
    
    await onSave(entities);
    } catch (e) {
      console.error('Save failed', e);
    } finally {
      setIsSaving(false);
    }
  };

  const formatPhone = (value: string) => {
    const cleaned = ('' + value).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return value;
    return !match[2] ? match[1] : `(${match[1]}) ${match[2]}` + (match[3] ? `-${match[3]}` : '');
  };

  const addTrustee = () => {
    setTrustees([...trustees, { id: generateId(), fullName: '', phone: '', email: '', address: '' }]);
  };

  const updateTrustee = (index: number, field: string, value: string) => {
    const newTrustees = [...trustees];
    newTrustees[index] = { ...newTrustees[index], [field]: field === 'phone' ? formatPhone(value) : value };
    setTrustees(newTrustees);
  };

  const removeTrustee = (index: number) => {
    const newTrustees = [...trustees];
    newTrustees.splice(index, 1);
    setTrustees(newTrustees);
  };

  const addMember = () => {
    setMembers([...members, { id: generateId(), fullName: '', role: 'Managing Member', phone: '', email: '', address: '' }]);
  };

  const updateMember = (index: number, field: string, value: string) => {
    const newMembers = [...members];
    newMembers[index] = { ...newMembers[index], [field]: field === 'phone' ? formatPhone(value) : value };
    setMembers(newMembers);
  };

  const removeMember = (index: number) => {
    const newMembers = [...members];
    newMembers.splice(index, 1);
    setMembers(newMembers);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl flex flex-col my-auto" style={{ maxHeight: '90vh' }}>
        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-xl font-bold text-[#141435] capitalize">{type === 'married' ? 'Married Couple' : type === 'company' ? 'LLC' : type} Info</h2>
          <button onClick={onClose} className="text-2xl leading-none">&times;</button>
        </div>
        
        <div className="p-6 overflow-y-auto">
          {type === 'company' && (
            <div className="flex flex-col gap-3 mb-6 p-4 border rounded bg-gray-50">
              <h3 className="font-bold text-[#141435]">LLC Details</h3>
              <div className="flex flex-col">
                <label className="text-xs text-[#485D7D] font-bold">Company / LLC Name</label>
                <input className="border rounded p-2 text-sm" placeholder="e.g., Acme Holdings, LLC" value={form1.name || ''} onChange={e => setForm1({...form1, name: e.target.value})} />
              </div>
              <div className="mt-4 border-t pt-4">
                <h3 className="font-bold text-[#141435] mb-2">Members</h3>
                {members.map((m, idx) => (
                    <div key={m.id || idx} className="flex flex-col gap-3 mb-4 p-4 border border-gray-200 rounded bg-white relative">
                        <button type="button" onClick={() => removeMember(idx)} className="absolute top-2 right-2 text-red-500 text-xs font-bold">Remove</button>
                        <div className="flex gap-3">
                            <div className="flex flex-col flex-1">
                                <label className="text-xs text-[#485D7D] font-bold">Full Name</label>
                                <input className="border rounded p-2 text-sm" value={m.fullName || ''} onChange={e => updateMember(idx, 'fullName', e.target.value)} />
                            </div>
                            <div className="flex flex-col w-48">
                                <label className="text-xs text-[#485D7D] font-bold">Role</label>
                                <select
                                  className="border rounded p-2 text-sm bg-white"
                                  value={m.role || 'Managing Member'}
                                  onChange={e => updateMember(idx, 'role', e.target.value)}
                                >
                                  {memberRoleOptions.map(roleOpt => (
                                    <option key={roleOpt} value={roleOpt}>{roleOpt}</option>
                                  ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <div className="flex flex-col flex-1">
                                <label className="text-xs text-[#485D7D] font-bold">Phone <span className="text-red-500">*</span></label>
                                <input className="border rounded p-2 text-sm" type="tel" maxLength={14} value={m.phone || ''} onChange={e => updateMember(idx, 'phone', e.target.value)} />
                            </div>
                            <div className="flex flex-col flex-1">
                                <label className="text-xs text-[#485D7D] font-bold">Email</label>
                                <input className="border rounded p-2 text-sm" type="email" value={m.email || ''} onChange={e => updateMember(idx, 'email', e.target.value)} />
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <label className="text-xs text-[#485D7D] font-bold">Full Address</label>
                            <AddressInput value={m.address || ''} onChange={val => updateMember(idx, 'address', val)} />
                        </div>
                    </div>
                ))}
                <button type="button" onClick={addMember} className="text-[#0B4BB6] text-sm font-bold mt-2">+ Add Member</button>
              </div>
            </div>
          )}
          {type === 'trust' && (
            <div className="flex flex-col gap-3 mb-6 p-4 border rounded bg-gray-50">
              <h3 className="font-bold text-[#141435]">Trust Details</h3>
              <div className="flex flex-col">
                <label className="text-xs text-[#485D7D] font-bold">Trust Name</label>
                <input className="border rounded p-2 text-sm" value={form1.name || ''} onChange={e => setForm1({...form1, name: e.target.value})} />
              </div>
              <div className="mt-4 border-t pt-4">
                <h3 className="font-bold text-[#141435] mb-2">Trustees</h3>
                {trustees.map((t, idx) => (
                    <div key={t.id || idx} className="flex flex-col gap-3 mb-4 p-4 border border-gray-200 rounded bg-white relative">
                        <button onClick={() => removeTrustee(idx)} className="absolute top-2 right-2 text-red-500 text-xs font-bold">Remove</button>
                        <div className="flex flex-col">
                            <label className="text-xs text-[#485D7D] font-bold">Full Name</label>
                            <input className="border rounded p-2 text-sm" value={t.fullName || ''} onChange={e => updateTrustee(idx, 'fullName', e.target.value)} />
                        </div>
                        <div className="flex gap-3">
                            <div className="flex flex-col flex-1">
                                <label className="text-xs text-[#485D7D] font-bold">Phone <span className="text-red-500">*</span></label>
                                <input className="border rounded p-2 text-sm" type="tel" maxLength={14} value={t.phone || ''} onChange={e => updateTrustee(idx, 'phone', e.target.value)} />
                            </div>
                            <div className="flex flex-col flex-1">
                                <label className="text-xs text-[#485D7D] font-bold">Email</label>
                                <input className="border rounded p-2 text-sm" type="email" value={t.email || ''} onChange={e => updateTrustee(idx, 'email', e.target.value)} />
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <label className="text-xs text-[#485D7D] font-bold">Full Address</label>
                            <AddressInput value={t.address || ''} onChange={val => updateTrustee(idx, 'address', val)} />
                        </div>
                    </div>
                ))}
                <button type="button" onClick={addTrustee} className="text-[#0B4BB6] text-sm font-bold mt-2">+ Add Trustee</button>
              </div>
            </div>
          )}

          {type === 'individual' && (
             <div className="flex flex-col gap-3 mb-4 p-4 border rounded bg-gray-50">
               <h3 className="font-bold text-[#141435]">Individual Details</h3>
               {isSplitNameMode ? (
                 <div className="grid grid-cols-3 gap-3">
                   <div className="flex flex-col">
                     <label className="text-xs text-[#485D7D] font-bold">First Name</label>
                     <input className="border rounded p-2 text-sm" value={form1.firstName || ''} onChange={e => setForm1({...form1, firstName: e.target.value})} />
                   </div>
                   <div className="flex flex-col">
                     <label className="text-xs text-[#485D7D] font-bold">Middle Name</label>
                     <input className="border rounded p-2 text-sm" value={form1.middleName || ''} onChange={e => setForm1({...form1, middleName: e.target.value})} />
                   </div>
                   <div className="flex flex-col">
                     <label className="text-xs text-[#485D7D] font-bold">Last Name</label>
                     <input className="border rounded p-2 text-sm" value={form1.lastName || ''} onChange={e => setForm1({...form1, lastName: e.target.value})} />
                   </div>
                 </div>
               ) : (
                 <div className="flex flex-col">
                   <label className="text-xs text-[#485D7D] font-bold">Full Name</label>
                   <input className="border rounded p-2 text-sm" value={form1.fullName || ''} onChange={e => setForm1({...form1, fullName: e.target.value})} />
                 </div>
               )}
               {showMaritalStatus && (
                 <div className="flex flex-col">
                   <label className="text-xs text-[#485D7D] font-bold">Marital Status</label>
                   <select
                     className="border rounded p-2 text-sm bg-white"
                     value={maritalStatus}
                     onChange={e => setMaritalStatus(e.target.value)}
                   >
                     {maritalOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                   </select>
                 </div>
               )}
               <h3 className="font-bold text-[#141435] mt-4 border-t pt-4">Contact Info</h3>
               <div className="flex gap-3">
                 <div className="flex flex-col flex-1">
                   <label className="text-xs text-[#485D7D] font-bold">Phone <span className="text-red-500">*</span></label>
                   <input className="border rounded p-2 text-sm" type="tel" maxLength={14} value={form1.phone || ''} onChange={e => setForm1({...form1, phone: formatPhone(e.target.value)})} />
                 </div>
                 <div className="flex flex-col flex-1">
                   <label className="text-xs text-[#485D7D] font-bold">Email</label>
                   <input className="border rounded p-2 text-sm" type="email" value={form1.email || ''} onChange={e => setForm1({...form1, email: e.target.value})} />
                 </div>
               </div>
               <div className="flex flex-col">
                 <label className="text-xs text-[#485D7D] font-bold">Full Address</label>
                 <AddressInput value={form1.address || ''} onChange={val => setForm1({...form1, address: val})} />
               </div>
               
               {allowNonParticipating && (
                 <div className="flex flex-col mt-3">
                   <label className="flex items-center gap-2 text-sm font-bold text-[#485D7D]">
                     <input 
                       type="checkbox" 
                       className="w-4 h-4 rounded border-gray-300 text-[#0B4BB6]"
                       checked={form1.isNonParticipating || false} 
                       onChange={e => setForm1({...form1, isNonParticipating: e.target.checked})} 
                     />
                     This person is a Non-Participating Grantor
                   </label>
                 </div>
               )}
             </div>
          )}

          {type === 'married' && (
             <>
               <div className="flex flex-col gap-3 mb-4 p-4 border rounded bg-gray-50">
                 <h3 className="font-bold text-[#141435]">Spouse 1 Details</h3>
                 {isSplitNameMode ? (
                   <div className="grid grid-cols-3 gap-3">
                     <div className="flex flex-col">
                       <label className="text-xs text-[#485D7D] font-bold">First Name</label>
                       <input className="border rounded p-2 text-sm" value={form1.firstName || ''} onChange={e => setForm1({...form1, firstName: e.target.value})} />
                     </div>
                     <div className="flex flex-col">
                       <label className="text-xs text-[#485D7D] font-bold">Middle Name</label>
                       <input className="border rounded p-2 text-sm" value={form1.middleName || ''} onChange={e => setForm1({...form1, middleName: e.target.value})} />
                     </div>
                     <div className="flex flex-col">
                       <label className="text-xs text-[#485D7D] font-bold">Last Name</label>
                       <input className="border rounded p-2 text-sm" value={form1.lastName || ''} onChange={e => setForm1({...form1, lastName: e.target.value})} />
                     </div>
                   </div>
                 ) : (
                   <div className="flex flex-col">
                     <label className="text-xs text-[#485D7D] font-bold">Full Name</label>
                     <input className="border rounded p-2 text-sm" value={form1.fullName || ''} onChange={e => setForm1({...form1, fullName: e.target.value})} />
                   </div>
                 )}
                 <div className="flex gap-3 mt-2">
                   <div className="flex flex-col flex-1">
                     <label className="text-xs text-[#485D7D] font-bold">Phone <span className="text-red-500">*</span></label>
                     <input className="border rounded p-2 text-sm" type="tel" maxLength={14} value={form1.phone || ''} onChange={e => setForm1({...form1, phone: formatPhone(e.target.value)})} />
                   </div>
                   <div className="flex flex-col flex-1">
                     <label className="text-xs text-[#485D7D] font-bold">Email</label>
                     <input className="border rounded p-2 text-sm" type="email" value={form1.email || ''} onChange={e => setForm1({...form1, email: e.target.value})} />
                   </div>
                 </div>
               </div>
               
               <div className="flex flex-col gap-3 mb-4 p-4 border rounded bg-gray-50">
                 <h3 className="font-bold text-[#141435]">Spouse 2 Details</h3>
                 {isSplitNameMode ? (
                   <div className="grid grid-cols-3 gap-3">
                     <div className="flex flex-col">
                       <label className="text-xs text-[#485D7D] font-bold">First Name</label>
                       <input className="border rounded p-2 text-sm" value={form2.firstName || ''} onChange={e => setForm2({...form2, firstName: e.target.value})} />
                     </div>
                     <div className="flex flex-col">
                       <label className="text-xs text-[#485D7D] font-bold">Middle Name</label>
                       <input className="border rounded p-2 text-sm" value={form2.middleName || ''} onChange={e => setForm2({...form2, middleName: e.target.value})} />
                     </div>
                     <div className="flex flex-col">
                       <label className="text-xs text-[#485D7D] font-bold">Last Name</label>
                       <input className="border rounded p-2 text-sm" value={form2.lastName || ''} onChange={e => setForm2({...form2, lastName: e.target.value})} />
                     </div>
                   </div>
                 ) : (
                   <div className="flex flex-col">
                     <label className="text-xs text-[#485D7D] font-bold">Full Name</label>
                     <input className="border rounded p-2 text-sm" value={form2.fullName || ''} onChange={e => setForm2({...form2, fullName: e.target.value})} />
                   </div>
                 )}
                 <div className="flex gap-3 mt-2">
                   <div className="flex flex-col flex-1">
                     <label className="text-xs text-[#485D7D] font-bold">Phone <span className="text-red-500">*</span></label>
                     <input className="border rounded p-2 text-sm" type="tel" maxLength={14} value={form2.phone || ''} onChange={e => setForm2({...form2, phone: formatPhone(e.target.value)})} />
                   </div>
                   <div className="flex flex-col flex-1">
                     <label className="text-xs text-[#485D7D] font-bold">Email</label>
                     <input className="border rounded p-2 text-sm" type="email" value={form2.email || ''} onChange={e => setForm2({...form2, email: e.target.value})} />
                   </div>
                 </div>
               </div>

               <div className="flex flex-col gap-3 mb-4 p-4 border rounded bg-gray-50">
                 <h3 className="font-bold text-[#141435]">Property Address</h3>
                 <p className="text-xs text-gray-500 mb-2">Shared address for both spouses.</p>
                 <div className="flex flex-col">
                   <label className="text-xs text-[#485D7D] font-bold">Full Address</label>
                   <AddressInput value={sharedContact.address || ''} onChange={val => setSharedContact({...sharedContact, address: val})} />
                 </div>
               </div>
             </>
          )}
        </div>

        <div className="p-6 border-t flex gap-4 sticky bottom-0 bg-white rounded-b-2xl z-10">
          <button className="flex-1 py-2 rounded-full border border-gray-300 font-bold" onClick={onClose}>Cancel</button>
          <button className="flex-1 py-2 rounded-full bg-[#0B4BB6] text-white font-bold" onClick={handleSave} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedGrantorModal;
